import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { NAV, FOLLOWUPS } from "@/constants/testIds";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";

const linkBase =
  "text-sm tracking-wide transition-colors duration-200 hover:text-primary";
const linkActive = "text-primary font-semibold";
const linkIdle = "text-foreground/70";

const SHARE_TOKENS_KEY = "imali:share-tokens";
const REMINDERS_POLL_INTERVAL_MS = 60_000;
const DUE_SOON_WINDOW_MS = 24 * 60 * 60 * 1000;
const loadShareTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(SHARE_TOKENS_KEY) || "{}");
  } catch {
    return {};
  }
};

export default function AppShell({ children }) {
  const { pathname } = useLocation();
  const isProposalPreview = pathname.startsWith("/proposals/") && !pathname.endsWith("/new");
  const [dueCount, setDueCount] = useState(0);

  // Silently tally due-or-overdue reminders across owned shares for the nav badge.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const tokens = loadShareTokens();
      const ids = Object.keys(tokens);
      if (ids.length === 0) {
        if (!cancelled) setDueCount(0);
        return;
      }
      const res = await Promise.allSettled(
        ids.map((id) =>
          api.get(`/reminders?share_id=${id}`, {
            headers: { "X-Owner-Token": tokens[id] },
          })
        )
      );
      if (cancelled) return;
      const now = Date.now();
      let n = 0;
      const soon = now + DUE_SOON_WINDOW_MS;
      for (const r of res) {
        if (r.status === "fulfilled") {
          for (const rem of r.value.data) {
            if (new Date(rem.remind_at).getTime() <= soon) n += 1;
          }
        }
      }
      setDueCount(n);
    };
    tick();
    const iv = setInterval(tick, REMINDERS_POLL_INTERVAL_MS);
    const onChange = () => tick();
    window.addEventListener("imali:reminders-changed", onChange);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("imali:reminders-changed", onChange);
    };
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center gap-2 font-display text-2xl tracking-tighter"
          >
            <span
              aria-hidden="true"
              className="inline-block w-2.5 h-2.5 rounded-full bg-secondary"
            />
            <span className="font-semibold text-primary">imali</span>
            <span className="hidden sm:inline text-xs text-muted-foreground tracking-[0.25em] uppercase ml-2">
              Grants · Loans · AI
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <NavLink
              to="/loans"
              data-testid={NAV.loans}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkIdle}`
              }
            >
              Business Loans
            </NavLink>
            <NavLink
              to="/compare"
              data-testid={NAV.compare}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkIdle}`
              }
            >
              Compare
            </NavLink>
            <NavLink
              to="/followups"
              data-testid={NAV.followups}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkIdle} inline-flex items-center gap-2`
              }
            >
              Follow-ups
              {dueCount > 0 && (
                <span
                  data-testid={FOLLOWUPS.countBadge}
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-secondary text-secondary-foreground text-[10px] font-mono font-semibold"
                >
                  {dueCount}
                </span>
              )}
            </NavLink>
            <NavLink
              to="/email-events"
              data-testid={NAV.emailEvents}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkIdle}`
              }
            >
              Email events
            </NavLink>
            <NavLink
              to="/proposals"
              data-testid={NAV.proposals}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkIdle}`
              }
            >
              My Proposals
            </NavLink>
          </nav>

          <Link to="/proposals/new" data-testid={NAV.new}>
            <Button
              size="sm"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <PlusCircle aria-hidden="true" className="w-4 h-4 mr-2" />
              New Proposal
            </Button>
          </Link>
        </div>
      </header>

      <main className={isProposalPreview ? "flex-1" : "flex-1"}>{children}</main>

      <footer className="border-t border-border/60 py-8 mt-16 bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-muted-foreground tracking-wide">
          <span>
            © {new Date().getFullYear()} Imali — Grant & Loan Writer AI
          </span>
          <span className="font-mono">
            Multi-currency · Construction · Manufacturing · Fintech · Commercial · Agribusiness · Engineering · Mining
          </span>
        </div>
      </footer>
    </div>
  );
}
